/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import Currency from "currency.js"
import {
  CURRENCY_SYMBOL_TO_CODE,
  getCurrencyCodeByCountryCode,
  getLocaleInfo,
} from "common/utils/locale"
import { storageHelper } from "./storage"

interface CurrencyInfo {
  code: string
  decimal: number
  separator: string
  symbol: string
  decimalSymbol: string
  exponent: number
}

export function getCurrencyInfo(currencyCode?: string): CurrencyInfo {
  const localeInfo = getLocaleInfo(currencyCode ?? "")
  return {
    code: localeInfo.currencyCode,
    decimal: localeInfo.decimals,
    exponent: localeInfo.exponentUnit,
    separator: localeInfo.thousandSymbol,
    symbol: localeInfo.currencySymbol,
    decimalSymbol: localeInfo.decimalSymbol,
  }
}

const defaultExponent = 2

export const minorUnitToCurrency = (value?: number, currency?: string) => {
  const exponent = getCurrencyInfo(currency ?? "").exponent ?? defaultExponent

  return Number(value ?? 0) / 10 ** exponent
}

export const currencyToMinorUnit = (
  value?: string | number,
  currency?: string,
) => {
  const exponent = getCurrencyInfo(currency).exponent ?? defaultExponent

  return parseFloat((Number(value ?? 0) * 10 ** exponent).toFixed(0))
}

export const getCurrencyInfoBySymbol = (
  currencySymbol: string,
): CurrencyInfo => {
  const code = CURRENCY_SYMBOL_TO_CODE[currencySymbol] ?? "SGD"
  return getCurrencyInfo(code)
}

/**
 * Parses a locale-formatted currency display string and returns the value in
 * minor units (smallest currency denomination) as a safe integer, avoiding
 * floating-point precision issues.
 *
 * Uses the currency symbol to resolve the correct thousand/decimal separators
 * and exponent for the conversion.
 *
 * | Symbol | Currency | Example display  | Minor unit result |
 * |--------|----------|------------------|-------------------|
 * | Rp     | IDR      | "8.000"          | 800000            |
 * | RM     | MYR      | "1,000.50"       | 100050            |
 * | ฿      | THB      | "1,000"          | 100000            |
 * | ₫      | VND      | "10.000"         | 10000             |
 * | ₱      | PHP      | "1,000.50"       | 100050            |
 * | S$     | SGD      | "10.50"          | 1050              |
 * | KS     | MMK      | "5,000"          | 500000            |
 * | ៛      | KHR      | "5,000"          | 500000            |
 */
export const parseCurrencyDisplayToMinorUnit = (
  displayValue: string,
  currencySymbol: string,
): number => {
  const info = getCurrencyInfoBySymbol(currencySymbol)
  const value = new Currency(displayValue, {
    symbol: info.symbol,
    separator: info.separator,
    decimal: info.decimalSymbol,
    precision: info.decimal,
  }).value
  return currencyToMinorUnit(value, info.code)
}

const currencyCode = getCurrencyCodeByCountryCode(
  storageHelper.getUserCountry() || "SG",
)
const currencyInfo = getCurrencyInfo(currencyCode)

// IDR -> 1000 -> 1.000
// SGD -> 1,01 -> 1.01
export const getAmountWithoutCurrencySymbol = (
  amount: number,
  precision?: number,
) => {
  return new Currency(amount, {
    symbol: currencyInfo.symbol,
    separator: currencyInfo.separator,
    precision: precision ?? currencyInfo.decimal,
    pattern: "#",
  }).format()
}

// IDR -> 1000 -> Rp1.000
// SGD -> 1,01 -> S$1.01
export const getAmountWithCurrencySymbol = (
  amount: number,
  precision?: number,
) => {
  return new Currency(amount, {
    symbol: currencyInfo.symbol,
    separator: currencyInfo.separator,
    precision: precision ?? currencyInfo.decimal,
  }).format()
}
